import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    Signal
} from '@angular/core';
import { Icon } from '../icon/icon';
import { IconType } from '../icon/icons';
import { MiniButton } from '../mini-button/mini-button';
import { ToastType } from './toast-type';

const TOAST_ICON_MAP: Record<ToastType, IconType> = {
    error: 'cancel-circle',
    info: 'info-circle',
    success: 'check-circle',
    warning: 'warning',
};

/**
 * The `<app-toast>` component is a feedback element that shows brief messages to the user.
 *
 * @remarks
 * Should not be used by itself. Use `ToastManager` instead. Purely visual — it carries no
 * `role`/live-region semantics of its own, since a screen reader can't reliably observe a
 * brand-new DOM node that already has both a live-region role and its text content in the same
 * mutation. `ToastManager` announces the message separately via `LiveAnnouncer`, which owns a
 * single persistent (pre-existing) live region for this exact reason.
 */
@Component({
    selector: 'app-toast',
    templateUrl: './toast.html',
    styleUrl: './toast.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        'class': 'app-toast',
        '[class.app-toast--type-error]': 'type() === "error"',
        '[class.app-toast--type-info]': 'type() === "info"',
        '[class.app-toast--type-success]': 'type() === "success"',
        '[class.app-toast--type-warning]': 'type() === "warning"',
    },
    imports: [
        Icon,
        MiniButton,
    ],
})
export class Toast {
    /**
     * The type of the toast.
     */
    public readonly type: InputSignal<ToastType> = input.required<ToastType>();

    /**
     * The title of the toast. Defaults to an empty string.
     */
    public readonly title: InputSignal<string> = input<string>('');

    /**
     * The message of the toast. Defaults to an empty string.
     */
    public readonly message: InputSignal<string> = input<string>('');

    /**
     * Emits when the toast should be closed.
     */
    public readonly closeToast: OutputEmitterRef<void> = output<void>();

    /**
     * The icon of the toast.
     */
    public readonly icon: Signal<IconType> = computed((): IconType => TOAST_ICON_MAP[this.type()]);
}